/**
 * Contenu des 16 landings thématiques — une entrée par thème.
 *
 * Ce fichier est la SEULE chose qui change d'une landing à l'autre :
 * la structure, les styles et les parcours vivent dans
 * pages/LandingThemePage.jsx, qui lit une entrée d'ici. Ajouter un
 * 17e thème = ajouter une entrée + une route + une entrée de sitemap,
 * rien d'autre.
 *
 * Les valeurs sont EXTRAITES des maquettes validées (16 exports HTML de
 * 17h33-17h34), pas retapées : un script a lu leur DOM et généré ce
 * fichier, pour qu'aucune coquille ne s'introduise entre la maquette et
 * le code.
 *
 * Écarts ASSUMÉS avec les maquettes, tous dans le même sens — retirer
 * du gabarit « PV » ce qui traînait sur des thèmes qui n'ont pas de PV :
 *  - la 4e vérification (« Les mentions obligatoires du procès-verbal »)
 *    est thématisée : le vrai document du thème, avec une mention qui
 *    existe réellement en droit belge ;
 *  - les deux premières réponses de FAQ nomment le document du thème,
 *    plus « ton PV ». Sur /pv, /vitesse et /telephone-volant le document
 *    EST un PV : elles sont inchangées ;
 *  - la 3e question de FAQ de /arnaque et /bail garde sa formulation
 *    thématique (« Et si le vendeur a disparu ? », « Et si le congé est
 *    parfaitement valable ? ») là où les maquettes de 17h33 étaient
 *    retombées sur la question générique ;
 *  - /vitesse : la 2e vérification dupliquait la 1re et la 5e répétait
 *    la mention « procès-verbal » — les deux sont thématisées ;
 *  - la 1re question de FAQ était « Je dois payer tout de suite ? » sur
 *    les seize thèmes. Elle ne le reste que là où le visiteur est bien
 *    celui à qui on réclame de l'argent (/pv, /vitesse,
 *    /telephone-volant, /huissier, /facture, /telecom, /energie, /bail).
 *    Sur les huit thèmes où c'est l'autre partie qui lui doit de l'argent
 *    (/caution, /licenciement, /sepa, /arnaque, /jamais-livre,
 *    /assurance, /sinistre-refuse, /abonnement-piege), la question porte
 *    sur ce qu'il peut récupérer, et la clause d'attaque de la réponse
 *    suit. Cette attaque ne fait que rappeler des faits déjà affirmés
 *    dans les vérifications ou les arguments du même thème — elle
 *    n'introduit aucune base légale nouvelle, et la queue
 *    « Scanne d'abord : … » est restée telle quelle ;
 *  - même logique passée sur les trois autres questions de FAQ. Le
 *    gabarit supposait partout un visiteur DÉBITEUR — qui doit de
 *    l'argent et cherche à ne pas payer. Là où il est CRÉANCIER, la
 *    démarche n'est plus « contester » mais « réclamer » (Q2 et Q4), et
 *    « Payer vite peut alors être ta meilleure option » (Q3) n'a plus de
 *    sens : c'est renoncer, pas payer, qui est l'arbitrage. /bail est un
 *    cas à part — on n'y réclame pas d'argent au visiteur, on lui demande
 *    de partir : ses questions parlent de délai de départ, pas de
 *    paiement.
 *
 * PRIX : une landing est une page d'acquisition, elle n'annonce qu'un
 * seul prix d'entrée — Starter à 34 €/mois avec l'engagement de douze
 * mois, 39 € sans. Deux champs le portent et doivent rester d'accord :
 * proof.juristPrice (« Dès 34 €/mois », sur la carte d'action, le prix
 * d'appel seul) et la 4e réponse de FAQ, qui donne LES DEUX chiffres —
 * parce qu'elle parle justement d'engagement, et qu'annoncer 34 € en
 * disant « sans engagement » serait faux. Une grille précédente a vu
 * /licenciement afficher un prix sur sa carte et un autre dans sa FAQ,
 * faute d'accord entre les deux champs : les deux prix se
 * contredisaient sur la même page.
 * Les autres montants en € des thèmes ne sont PAS des tarifs Archer :
 * ce sont les enjeux du litige (KPIs, titres, h1).
 *
 * Ce qui est COMMUN à tous les thèmes (sous-titre du hero, libellés de
 * la dropzone, les 3 cartes d'action, les statistiques du bloc IA, la
 * section LA PREUVE hors données, le pied de la carte d'analyse) n'est
 * pas ici : c'est dans le composant, une seule fois.
 */

export const LANDING_THEMES = {
  'pv': {
    badge: '🚗 PV & amendes de vitesse',
    h1: ['Tu as reçu un PV ?', 'Ne paie pas avant de savoir.'],
    dropzone: 'Glisse ton PV ici, ou clique',
    checksTitle: 'Ce qu\'Archer vérifie sur ton PV',
    checksIntro: 'Un seul de ces défauts peut suffire à faire tomber le PV',
    checks: [
      ['Le certificat d\'étalonnage du radar', '· périmé, le PV est fragile'],
      ['La signalisation de la zone', '· absente ou non répétée, ça se conteste'],
      ['La marge de tolérance appliquée', '· elle est obligatoire'],
      ['Les mentions obligatoires du procès-verbal', '· date, heure et lieu du constat, identité du verbalisant'],
      ['L\'identification du conducteur', ''],
      ['Le montant réclamé', 'face au barème officiel'],
    ],
    legalNote: 'Chaque vérification s\'appuie sur la législation routière belge. Chaque référence citée est vérifiée.',
    aiTail: 'Aucun chatbot générique ne lit ton PV comme elle.',
    bandLead: 'L\'IA analyse ton PV en 7 passes.',
    faqTitle: 'Tes questions sur les PV',
    faqs: [
      { q: 'Je dois payer tout de suite ?', a: 'Non. Et payer trop vite peut valoir acceptation. Scanne d\'abord : tu sauras si ton PV est contestable et combien de temps tu as.' },
      { q: 'C\'est légal de contester ?', a: 'Oui, c\'est un droit. La contestation suit une procédure précise : Archer te la donne, adaptée à ton PV.' },
      { q: 'Et si mon PV est incontestable ?', a: 'Archer te le dit franchement. Payer vite peut alors être ta meilleure option, et tu auras évité de payer un avocat pour l\'apprendre.' },
      { q: 'Combien ça coûte ?', a: 'Le scan et le verdict : gratuits, sans carte. Si tu veux qu\'Archer conteste pour toi : dès 34€/mois avec l’engagement 12 mois, 39€ sans engagement.' },
    ],
    finalCta: ['Ton PV a des délais.', 'Chaque jour compte.'],
    finalCtaBtn: 'Analyser mon PV · Gratuit',
    proof: {
      score: 78,
      ringOffset: 72,
      ringColor: '#16a34a',
      kickerKind: 'g',
      kicker: '● PV CONTESTABLE',
      headline: 'Trois failles exploitables.',
      headlineKind: 'g',
      summary: 'Ton PV présente des défauts de forme réels : le certificat d\'étalonnage du radar n\'est pas mentionné, la marge de tolérance semble mal appliquée, et la signalisation de la zone n\'est pas justifiée.',
      kpis: [
        { label: 'Montant réclamé', value: '179 €', kind: '', sub: 'amende + frais' },
        { label: 'Si le PV tombe', value: '0 €', kind: 'g', sub: 'tu ne paies rien' },
        { label: 'Délai pour contester', value: '12 jours', kind: 'a', sub: 'depuis ton document' },
      ],
      args: [
        { title: 'Le certificat de vérification annuelle du cinémomètre n\'est pas produit', basis: 'AR du 12 octobre 2010 · homologation et contrôle annuel', tag: 'SOLIDE', weak: false },
        { title: 'La marge de tolérance technique ne semble pas appliquée', basis: 'AR 12/10/2010 · la vitesse retenue doit être corrigée', tag: 'SOLIDE', weak: false },
        { title: 'La signalisation de la zone n\'est pas établie dans le PV', basis: 'Art. 62 Loi du 16 mars 1968 · force probante du PV', tag: 'MOYEN', weak: true },
      ],
      letter: [
        'La demande du certificat d\'étalonnage du radar',
        'Le recalcul de la vitesse après marge de tolérance',
        'La contestation formelle dans le délai légal, en recommandé',
        'La demande de classement sans suite motivée',
      ],
      cta: 'Analyser mon PV · Gratuit',
      projection: [
        { label: 'Annulation', value: '45%', width: '45%', color: '#16a34a' },
        { label: 'Réduction', value: '30%', width: '30%', color: '#f59e0b' },
        { label: 'Maintien', value: '25%', width: '25%', color: '#e2e0db' },
      ],
      note: 'Et si ton PV est solide, Archer te le dit franchement : tu paies vite, sans frais en plus.',
      foot: ['ANALYSE GÉNÉRÉE EN 58 SECONDES', '4 RÉFÉRENCES LÉGALES VÉRIFIÉES · DROIT BELGE'],
      juristPrice: 'Dès 34 €/mois',
    },
  },
  'huissier': {
    badge: '📨 Courrier d\'huissier',
    h1: ['Un huissier t\'écrit ?', 'Vérifie ce qu\'il peut vraiment faire.'],
    dropzone: 'Glisse ton courrier ici, ou clique',
    checksTitle: 'Ce qu\'Archer vérifie sur ton courrier',
    checksIntro: 'Un seul de ces défauts peut suffire à faire tomber la créance',
    checks: [
      ['La nature du courrier', '· amiable ou judiciaire, la différence est énorme'],
      ['Les frais réclamés', '· souvent gonflés, contestables'],
      ['La prescription de la dette', '· une dette trop ancienne n\'est plus exigible'],
      ['Les mentions obligatoires de la mise en demeure', '· identité du créancier, origine et détail de la dette'],
      ['La possibilité réelle de saisie', ''],
      ['Le montant réclamé', 'face au barème officiel'],
    ],
    legalNote: 'Chaque vérification s\'appuie sur la législation belge du recouvrement. Chaque référence citée est vérifiée.',
    aiTail: 'Aucun chatbot générique ne lit ta mise en demeure comme elle.',
    bandLead: 'L\'IA analyse ton courrier en 7 passes.',
    faqTitle: 'Tes questions sur les huissiers',
    faqs: [
      { q: 'Je dois payer tout de suite ?', a: 'Non. Et payer trop vite peut valoir acceptation. Scanne d\'abord : tu sauras si la créance réclamée est contestable et combien de temps tu as.' },
      { q: 'C\'est légal de contester ?', a: 'Oui, c\'est un droit. La contestation suit une procédure précise : Archer te la donne, adaptée à ta mise en demeure.' },
      { q: 'Et si mon dossier est solide côté adverse ?', a: 'Archer te le dit franchement. Payer vite peut alors être ta meilleure option, et tu auras évité de payer un avocat pour l\'apprendre.' },
      { q: 'Combien ça coûte ?', a: 'Le scan et le verdict : gratuits, sans carte. Si tu veux qu\'Archer conteste pour toi : dès 34€/mois avec l’engagement 12 mois, 39€ sans engagement.' },
    ],
    finalCta: ['Ce courrier a des délais.', 'Chaque jour compte.'],
    finalCtaBtn: 'Analyser mon courrier · Gratuit',
    proof: {
      score: 72,
      ringOffset: 91,
      ringColor: '#16a34a',
      kickerKind: 'g',
      kicker: '● MISE EN DEMEURE AMIABLE',
      headline: 'Il ne peut rien saisir. Pas encore.',
      headlineKind: 'g',
      summary: 'Ce courrier est une mise en demeure amiable, pas une procédure judiciaire. Aucune saisie n\'est possible sans jugement, et les frais réclamés dépassent le barème légal.',
      kpis: [
        { label: 'Montant réclamé', value: '410 €', kind: '', sub: 'dont frais de recouvrement' },
        { label: 'Frais contestables', value: '95 €', kind: 'g', sub: 'dépassement du barème' },
        { label: 'Délai pour réagir', value: '8 jours', kind: 'a', sub: 'avant relance' },
      ],
      args: [
        { title: 'Les frais de recouvrement dépassent le plafond légal', basis: 'Loi du 20 décembre 2002 sur le recouvrement amiable', tag: 'SOLIDE', weak: false },
        { title: 'Aucune preuve de la créance d\'origine n\'est jointe', basis: 'Art. 1315 ancien Code civil · charge de la preuve', tag: 'SOLIDE', weak: false },
        { title: 'Le délai de prescription pourrait être atteint', basis: 'Art. 2262bis ancien Code civil', tag: 'MOYEN', weak: true },
      ],
      letter: [
        'La contestation des frais excédant le barème légal',
        'La demande de preuve de la créance d\'origine',
        'Le rappel qu\'aucune saisie n\'est possible sans jugement',
        'La demande d\'un plan de paiement si la dette est due',
      ],
      cta: 'Analyser mon courrier · Gratuit',
      projection: [
        { label: 'Frais réduits', value: '48%', width: '48%', color: '#16a34a' },
        { label: 'Dette confirmée', value: '32%', width: '32%', color: '#f59e0b' },
        { label: 'Sans réponse', value: '20%', width: '20%', color: '#e2e0db' },
      ],
      note: 'Et si la dette est bien la tienne, Archer te le dit : tu sauras quoi payer, au prix juste.',
      foot: ['ANALYSE GÉNÉRÉE EN 58 SECONDES', '4 RÉFÉRENCES LÉGALES VÉRIFIÉES · DROIT BELGE'],
      juristPrice: 'Dès 34 €/mois',
    },
  },
  'caution': {
    badge: '🏠 Garantie locative',
    h1: ['Ton proprio garde ta caution ?', 'Il n\'a peut-être pas le droit.'],
    dropzone: 'Glisse ton document ici, ou clique',
    checksTitle: 'Ce qu\'Archer vérifie sur ta caution',
    checksIntro: 'Un seul de ces défauts peut suffire à faire tomber la retenue',
    checks: [
      ['L\'usure normale vs les dégâts', '· la limite légale est précise'],
      ['L\'état des lieux de sortie', '· comparé point par point à l\'entrée'],
      ['Les justificatifs de retenue', '· factures ou devis obligatoires'],
      ['Les mentions obligatoires de l\'état des lieux', '· établi contradictoirement, daté et signé par les deux parties'],
      ['La proportionnalité de la retenue', ''],
      ['Le montant réclamé', 'face au barème officiel'],
    ],
    legalNote: 'Chaque vérification s\'appuie sur la législation belge du bail. Chaque référence citée est vérifiée.',
    aiTail: 'Aucun chatbot générique ne lit ton état des lieux comme elle.',
    bandLead: 'L\'IA analyse ton dossier en 7 passes.',
    faqTitle: 'Tes questions sur la caution',
    faqs: [
      { q: 'Ils peuvent garder ma garantie ?', a: 'Seulement pour des dégâts justifiés, factures ou devis à l\'appui, jamais pour de l\'usure normale. Scanne d\'abord : tu sauras si la retenue sur ta garantie locative est contestable et combien de temps tu as.' },
      { q: 'J\'ai le droit de contester la retenue ?', a: 'Oui, c\'est un droit. La contestation suit une procédure précise : Archer te la donne, adaptée à ton état des lieux de sortie.' },
      { q: 'Et si mon dossier est solide côté adverse ?', a: 'Archer te le dit franchement. Renoncer peut alors être le bon calcul, et tu auras évité de payer un avocat pour l\'apprendre.' },
      { q: 'Combien ça coûte ?', a: 'Le scan et le verdict : gratuits, sans carte. Si tu veux qu\'Archer conteste pour toi : dès 34€/mois avec l’engagement 12 mois, 39€ sans engagement.' },
    ],
    finalCta: ['Ta caution a des délais.', 'Chaque jour compte.'],
    finalCtaBtn: 'Analyser ma caution · Gratuit',
    proof: {
      score: 75,
      ringOffset: 82,
      ringColor: '#16a34a',
      kickerKind: 'g',
      kicker: '● RETENUE CONTESTABLE',
      headline: '1 850 € qui te reviennent.',
      headlineKind: 'g',
      summary: 'La comparaison entre l\'état des lieux d\'entrée et de sortie montre une usure normale sur la quasi-totalité des points retenus. Aucune facture ne justifie le montant déduit.',
      kpis: [
        { label: 'Caution retenue', value: '1 850 €', kind: '', sub: 'sur 2 100 € déposés' },
        { label: 'Ce que tu récupères', value: '1 850 €', kind: 'g', sub: 'aucun justificatif' },
        { label: 'Délai pour agir', value: '2 mois', kind: 'a', sub: 'avant prescription' },
      ],
      args: [
        { title: 'Les retenues concernent de l\'usure normale, non facturable', basis: 'Art. 1730 ancien Code civil · obligations à la sortie', tag: 'SOLIDE', weak: false },
        { title: 'Aucune facture ni devis ne justifie le montant retenu', basis: 'Loi du 20 février 1991, art. 10 · preuve écrite', tag: 'SOLIDE', weak: false },
        { title: 'Le délai de restitution du solde est dépassé', basis: 'Jurisprudence constante des justices de paix', tag: 'MOYEN', weak: true },
      ],
      letter: [
        'La demande de restitution intégrale sous 15 jours',
        'La contestation point par point de l\'état des lieux',
        'La demande des justificatifs manquants',
        'Le rappel des intérêts dus sur le compte bloqué',
      ],
      cta: 'Analyser ma caution · Gratuit',
      projection: [
        { label: 'Restitution totale', value: '50%', width: '50%', color: '#16a34a' },
        { label: 'Restitution partielle', value: '32%', width: '32%', color: '#f59e0b' },
        { label: 'Refus', value: '18%', width: '18%', color: '#e2e0db' },
      ],
      note: 'Et si les retenues sont justifiées, Archer te le dit clairement : tu sauras exactement pourquoi.',
      foot: ['ANALYSE GÉNÉRÉE EN 58 SECONDES', '4 RÉFÉRENCES LÉGALES VÉRIFIÉES · DROIT BELGE'],
      juristPrice: 'Dès 34 €/mois',
    },
  },
  'licenciement': {
    badge: '💼 Licenciement & rupture',
    h1: ['Tu viens d\'être licencié ?', 'Vérifie ce qu\'on te doit avant de signer.'],
    dropzone: 'Glisse ta lettre ou ton C4 ici, ou clique',
    checksTitle: 'Ce qu\'Archer vérifie sur ton licenciement',
    checksIntro: 'Un seul de ces points peut représenter plusieurs milliers d\'euros',
    checks: [
      ['Le préavis respecté', '· la durée légale selon ton ancienneté'],
      ['L\'indemnité compensatoire', '· le calcul exact si le préavis n\'est pas presté'],
      ['Le motif invoqué', '· un licenciement déraisonnable est indemnisable'],
      ['Les mentions obligatoires de la lettre de congé', '· début et durée du préavis, forme légale respectée'],
      ['La procédure CCT 109 respectée', ''],
      ['Le montant réclamé', 'face au barème officiel'],
    ],
    legalNote: 'Chaque vérification s\'appuie sur la législation belge du travail. Chaque référence citée est vérifiée.',
    aiTail: 'Aucun chatbot générique ne lit ton C4 comme elle.',
    bandLead: 'L\'IA analyse ton dossier en 7 passes.',
    faqTitle: 'Tes questions sur le licenciement',
    faqs: [
      { q: 'J\'ai droit à quoi ?', a: 'Au préavis ou à son indemnité compensatoire, au pécule de vacances de sortie, et à la motivation de ton licenciement si tu la demandes. Scanne d\'abord : tu sauras si ta lettre de licenciement est contestable et combien de temps tu as.' },
      { q: 'J\'ai le droit de réclamer ?', a: 'Oui, c\'est un droit. La réclamation suit une procédure précise : Archer te la donne, adaptée à ton C4.' },
      { q: 'Et si mon dossier est solide côté adverse ?', a: 'Archer te le dit franchement. Renoncer peut alors être le bon calcul, et tu auras évité de payer un avocat pour l\'apprendre.' },
      { q: 'Combien ça coûte ?', a: 'Le scan et le verdict : gratuits, sans carte. Si tu veux qu\'Archer réclame pour toi : dès 34€/mois avec l’engagement 12 mois, 39€ sans engagement.' },
    ],
    finalCta: ['Ton dossier a des délais.', 'Chaque jour compte.'],
    finalCtaBtn: 'Analyser mon C4 · Gratuit',
    proof: {
      score: 71,
      ringOffset: 95,
      ringColor: '#16a34a',
      kickerKind: 'g',
      kicker: '● INDEMNITÉS DUES',
      headline: 'Il te doit encore 2 400 €.',
      headlineKind: 'g',
      summary: 'Ton préavis de 3 mois n\'a été presté que partiellement selon tes fiches de paie. Le solde n\'apparaît nulle part. Le pécule de vacances de sortie n\'a pas non plus été versé.',
      kpis: [
        { label: 'Solde versé', value: '11 200 €', kind: '', sub: 'à ce jour' },
        { label: 'Ce qu\'il te doit', value: '13 600 €', kind: 'g', sub: 'calcul complet' },
        { label: 'Délai pour agir', value: '34 jours', kind: 'a', sub: 'avant prescription' },
      ],
      args: [
        { title: 'Préavis incomplet · 6 semaines non couvertes', basis: 'Loi du 3 juillet 1978, art. 37 · calcul selon l\'ancienneté', tag: 'SOLIDE', weak: false },
        { title: 'Pécule de vacances de sortie non versé', basis: 'AR du 30 mars 1967 · paiement obligatoire en fin de contrat', tag: 'SOLIDE', weak: false },
        { title: 'Absence de motivation formelle du licenciement', basis: 'CCT n°109 · droit à la motivation', tag: 'MOYEN', weak: true },
      ],
      letter: [
        'La demande chiffrée du solde de préavis manquant',
        'La demande du pécule de vacances de sortie',
        'La demande de motivation formelle du licenciement',
        'Le délai de réponse et les suites en cas de silence',
      ],
      cta: 'Analyser mon C4 · Gratuit',
      projection: [
        { label: 'Obtention totale', value: '40%', width: '40%', color: '#16a34a' },
        { label: 'Obtention partielle', value: '40%', width: '40%', color: '#f59e0b' },
        { label: 'Refus', value: '20%', width: '20%', color: '#e2e0db' },
      ],
      note: 'Et si ton solde de tout compte est correct, Archer te le confirme, pas de réclamation inutile.',
      foot: ['ANALYSE GÉNÉRÉE EN 58 SECONDES', '4 RÉFÉRENCES LÉGALES VÉRIFIÉES · DROIT BELGE'],
      juristPrice: 'Dès 34 €/mois',
    },
  },
  'facture': {
    badge: '🧾 Facture contestée',
    h1: ['Une facture qui ne colle pas ?', 'Ne paie pas avant d\'avoir vérifié.'],
    dropzone: 'Glisse ta facture ici, ou clique',
    checksTitle: 'Ce qu\'Archer vérifie sur ta facture',
    checksIntro: 'Un seul de ces défauts peut suffire à faire tomber la facture',
    checks: [
      ['Les frais cachés', '· activation, résiliation, services non demandés'],
      ['La cohérence avec ton contrat', '· le prix facturé correspond-il à ce que tu as signé'],
      ['Les prélèvements SEPA non autorisés', '· remboursement automatique sous 8 semaines'],
      ['Les mentions obligatoires de la facture', '· identité du prestataire, détail des prestations, TVA'],
      ['L\'indexation appliquée', ''],
      ['Le montant réclamé', 'face au barème officiel'],
    ],
    legalNote: 'Chaque vérification s\'appuie sur la Code de droit économique belge. Chaque référence citée est vérifiée.',
    aiTail: 'Aucun chatbot générique ne lit ta facture comme elle.',
    bandLead: 'L\'IA analyse ta facture en 7 passes.',
    faqTitle: 'Tes questions sur les factures',
    faqs: [
      { q: 'Je dois payer tout de suite ?', a: 'Non. Et payer trop vite peut valoir acceptation. Scanne d\'abord : tu sauras si ta facture est contestable et combien de temps tu as.' },
      { q: 'C\'est légal de contester ?', a: 'Oui, c\'est un droit. La contestation suit une procédure précise : Archer te la donne, adaptée à ta facture.' },
      { q: 'Et si mon dossier est solide côté adverse ?', a: 'Archer te le dit franchement. Payer vite peut alors être ta meilleure option, et tu auras évité de payer un avocat pour l\'apprendre.' },
      { q: 'Combien ça coûte ?', a: 'Le scan et le verdict : gratuits, sans carte. Si tu veux qu\'Archer conteste pour toi : dès 34€/mois avec l’engagement 12 mois, 39€ sans engagement.' },
    ],
    finalCta: ['Ta facture a des délais.', 'Chaque jour compte.'],
    finalCtaBtn: 'Analyser ma facture · Gratuit',
    proof: {
      score: 74,
      ringOffset: 85,
      ringColor: '#16a34a',
      kickerKind: 'g',
      kicker: '● FACTURE CONTESTABLE',
      headline: '247 € de frais injustifiés.',
      headlineKind: 'g',
      summary: 'Ton opérateur facture des frais de résiliation alors que ton contrat initial de 24 mois est dépassé depuis 3 mois : la résiliation aurait dû être gratuite.',
      kpis: [
        { label: 'Montant réclamé', value: '247 €', kind: '', sub: 'frais de résiliation' },
        { label: 'Si la facture tombe', value: '0 €', kind: 'g', sub: 'tu ne paies rien' },
        { label: 'Délai pour contester', value: '30 jours', kind: 'a', sub: 'depuis ton document' },
      ],
      args: [
        { title: 'Contrat initial expiré depuis 3 mois · résiliation gratuite due', basis: 'Art. 111/3 §3, Loi du 13 juin 2005', tag: 'SOLIDE', weak: false },
        { title: 'Frais de résiliation non détaillés sur la facture', basis: 'Art. VI.83 CDE · clauses abusives', tag: 'SOLIDE', weak: false },
        { title: 'Aucun accord signé pour le renouvellement automatique', basis: 'Art. VI.45 CDE · information préalable', tag: 'MOYEN', weak: true },
      ],
      letter: [
        'La contestation formelle des frais de résiliation',
        'La demande de justification détaillée du montant',
        'La demande de remboursement sous 30 jours',
        'La mise en demeure en cas de silence',
      ],
      cta: 'Analyser ma facture · Gratuit',
      projection: [
        { label: 'Remboursement total', value: '50%', width: '50%', color: '#16a34a' },
        { label: 'Remboursement partiel', value: '28%', width: '28%', color: '#f59e0b' },
        { label: 'Refus', value: '22%', width: '22%', color: '#e2e0db' },
      ],
      note: 'Et si les frais sont justifiés, Archer te le dit clairement : tu sauras exactement pourquoi tu paies.',
      foot: ['ANALYSE GÉNÉRÉE EN 58 SECONDES', '4 RÉFÉRENCES LÉGALES VÉRIFIÉES · DROIT BELGE'],
      juristPrice: 'Dès 34 €/mois',
    },
  },
  'arnaque': {
    badge: '🛒 Achat en ligne',
    h1: ['Jamais livré, jamais remboursé ?', 'Tu as plus de droits que tu ne crois.'],
    dropzone: 'Glisse ta preuve d\'achat ici, ou clique',
    checksTitle: 'Ce qu\'Archer vérifie sur ta commande',
    checksIntro: 'Un seul de ces points peut suffire à obtenir ton remboursement',
    checks: [
      ['Le délai de livraison légal', '· 30 jours maximum, au-delà tu peux exiger le remboursement'],
      ['Le droit de rétractation', '· 14 jours, même sans motif'],
      ['La garantie légale de conformité', '· 2 ans, même si le vendeur ne la mentionne pas'],
      ['Les mentions obligatoires de la confirmation de commande', '· confirmation du contrat sur support durable, prix total'],
      ['L\'identité vérifiable du vendeur', ''],
      ['Le montant réclamé', 'face au barème officiel'],
    ],
    legalNote: 'Chaque vérification s\'appuie sur la Code de droit économique belge. Chaque référence citée est vérifiée.',
    aiTail: 'Aucun chatbot générique ne lit ta commande comme elle.',
    bandLead: 'L\'IA analyse ton dossier en 7 passes.',
    faqTitle: 'Tes questions sur les arnaques en ligne',
    faqs: [
      { q: 'Je peux être remboursé ?', a: 'Oui si le vendeur n\'a pas livré dans les 30 jours, et de toute façon dans les 14 jours de rétractation, même sans motif. Scanne d\'abord : tu sauras si ta commande ouvre droit à un remboursement et combien de temps tu as.' },
      { q: 'J\'ai le droit d\'exiger le remboursement ?', a: 'Oui, c\'est un droit. La réclamation suit une procédure précise : Archer te la donne, adaptée à ta commande et à ta preuve d\'achat.' },
      { q: 'Et si le vendeur a disparu ?', a: 'Archer te le dit franchement. La contestation bancaire, elle, ne dépend pas de lui, et tu auras évité de payer un avocat pour l\'apprendre.' },
      { q: 'Combien ça coûte ?', a: 'Le scan et le verdict : gratuits, sans carte. Si tu veux qu\'Archer réclame pour toi : dès 34€/mois avec l’engagement 12 mois, 39€ sans engagement.' },
    ],
    finalCta: ['Ton dossier a des délais.', 'Chaque jour compte.'],
    finalCtaBtn: 'Analyser ma commande · Gratuit',
    proof: {
      score: 81,
      ringOffset: 62,
      ringColor: '#16a34a',
      kickerKind: 'g',
      kicker: '● REMBOURSEMENT DÛ',
      headline: 'Le vendeur est en tort, pas toi.',
      headlineKind: 'g',
      summary: 'Ta commande devait être livrée sous 30 jours : 52 jours se sont écoulés sans nouvelle. Le droit de résolution du contrat est ouvert, avec remboursement intégral.',
      kpis: [
        { label: 'Montant réclamé', value: '389 €', kind: '', sub: 'commande non livrée' },
        { label: 'Ce que tu récupères', value: '389 €', kind: 'g', sub: 'remboursement intégral' },
        { label: 'Délai pour agir', value: 'Immédiat', kind: 'a', sub: 'délai légal dépassé' },
      ],
      args: [
        { title: 'Livraison non effectuée après 52 jours', basis: 'Art. VI.43 CDE · obligation de livraison sous 30 jours', tag: 'SOLIDE', weak: false },
        { title: 'Aucune réponse du vendeur aux relances', basis: 'Art. VI.44 CDE · droit à la résolution du contrat', tag: 'SOLIDE', weak: false },
        { title: 'Absence de mentions légales vérifiables sur le site', basis: 'Art. VI.45 CDE · obligation d\'information', tag: 'MOYEN', weak: true },
      ],
      letter: [
        'La mise en demeure de livrer sous 8 jours',
        'La demande de résolution et remboursement intégral',
        'La référence aux articles applicables du CDE',
        'La procédure de contestation bancaire si silence',
      ],
      cta: 'Analyser ma commande · Gratuit',
      projection: [
        { label: 'Remboursement obtenu', value: '55%', width: '55%', color: '#16a34a' },
        { label: 'Remboursement partiel', value: '20%', width: '20%', color: '#f59e0b' },
        { label: 'Sans réponse', value: '25%', width: '25%', color: '#e2e0db' },
      ],
      note: 'Et si ta commande est encore dans les délais, Archer te le dit, pas de démarche inutile.',
      foot: ['ANALYSE GÉNÉRÉE EN 58 SECONDES', '4 RÉFÉRENCES LÉGALES VÉRIFIÉES · DROIT BELGE'],
      juristPrice: 'Dès 34 €/mois',
    },
  },
  'bail': {
    badge: '🏠 Bail & logement',
    h1: ['Ton propriétaire te met dehors ?', 'Vérifie s\'il en a le droit.'],
    dropzone: 'Glisse ton courrier ici, ou clique',
    checksTitle: 'Ce qu\'Archer vérifie sur ton bail',
    checksIntro: 'Un seul de ces défauts peut suffire à faire tomber le congé',
    checks: [
      ['Le préavis de congé', '· durée légale et motifs autorisés selon la région'],
      ['La forme du congé', '· recommandé obligatoire, sinon nul'],
      ['L\'augmentation de loyer', '· l\'indexation correcte, hors clauses abusives'],
      ['Les mentions obligatoires du bail écrit', '· loyer, durée, désignation du bien et enregistrement'],
      ['L\'état des lieux : usure vs dégâts', ''],
      ['Le montant réclamé', 'face au barème officiel'],
    ],
    legalNote: 'Chaque vérification s\'appuie sur la législation régionale du bail. Chaque référence citée est vérifiée.',
    aiTail: 'Aucun chatbot générique ne lit ton bail comme elle.',
    bandLead: 'L\'IA analyse ton bail en 7 passes.',
    faqTitle: 'Tes questions sur le bail',
    faqs: [
      { q: 'Je dois partir tout de suite ?', a: 'Non. Et quitter les lieux trop vite peut valoir acceptation du congé. Scanne d\'abord : tu sauras si le congé reçu est contestable et combien de temps tu as.' },
      { q: 'C\'est légal de contester ?', a: 'Oui, c\'est un droit. La contestation suit une procédure précise : Archer te la donne, adaptée à ton congé et à ton bail.' },
      { q: 'Et si le congé est parfaitement valable ?', a: 'Archer te le dit franchement. Tu sauras alors à quelle date tu dois vraiment partir, et tu auras évité de payer un avocat pour l\'apprendre.' },
      { q: 'Combien ça coûte ?', a: 'Le scan et le verdict : gratuits, sans carte. Si tu veux qu\'Archer conteste pour toi : dès 34€/mois avec l’engagement 12 mois, 39€ sans engagement.' },
    ],
    finalCta: ['Ton congé a des délais.', 'Chaque jour compte.'],
    finalCtaBtn: 'Analyser mon congé · Gratuit',
    proof: {
      score: 69,
      ringOffset: 101,
      ringColor: '#f59e0b',
      kickerKind: 'a',
      kicker: '● CONGÉ IRRÉGULIER',
      headline: 'Le préavis ne respecte pas la forme légale.',
      headlineKind: 'a',
      summary: 'Le congé donné par ton propriétaire ne mentionne pas le motif requis pour un congé anticipé, et le délai notifié est plus court que le minimum légal applicable à ton bail.',
      kpis: [
        { label: 'Délai notifié', value: '60 jours', kind: '', sub: 'exigé par le courrier' },
        { label: 'Délai réellement dû', value: '6 mois', kind: 'g', sub: 'minimum légal' },
        { label: 'Délai pour réagir', value: '15 jours', kind: 'a', sub: 'avant effet' },
      ],
      args: [
        { title: 'Motif de congé anticipé non justifié dans le courrier', basis: 'Loi bail résidentiel, art. 3 §5 · motifs encadrés', tag: 'SOLIDE', weak: false },
        { title: 'Délai de préavis inférieur au minimum légal', basis: 'Art. 3 §2 · durée minimale selon le motif', tag: 'SOLIDE', weak: false },
        { title: 'Absence d\'envoi en recommandé', basis: 'Art. 3 §1 · forme obligatoire, sinon nullité', tag: 'MOYEN', weak: true },
      ],
      letter: [
        'La contestation formelle du préavis irrégulier',
        'La demande de respect du délai légal minimum',
        'Le rappel de la nullité en cas de vice de forme',
        'Les prochaines étapes en cas de désaccord',
      ],
      cta: 'Analyser mon congé · Gratuit',
      projection: [
        { label: 'Congé annulé', value: '42%', width: '42%', color: '#16a34a' },
        { label: 'Délai renégocié', value: '33%', width: '33%', color: '#f59e0b' },
        { label: 'Congé maintenu', value: '25%', width: '25%', color: '#e2e0db' },
      ],
      note: 'Et si le congé est parfaitement valable, Archer te le dit, tu t\'organises sereinement.',
      foot: ['ANALYSE GÉNÉRÉE EN 58 SECONDES', '4 RÉFÉRENCES LÉGALES VÉRIFIÉES · DROIT BELGE'],
      juristPrice: 'Dès 34 €/mois',
    },
  },
  'assurance': {
    badge: '🛡️ Litige assurance',
    h1: ['Ton assureur refuse de payer ?', 'Fais vérifier son refus.'],
    dropzone: 'Glisse la lettre de refus ici, ou clique',
    checksTitle: 'Ce qu\'Archer vérifie sur ton refus',
    checksIntro: 'Un seul de ces défauts peut suffire à faire tomber le refus',
    checks: [
      ['La motivation du refus', '· doit être précise, pas juste une clause citée'],
      ['La clause invoquée', '· sa validité et son application réelle'],
      ['Le délai de traitement', '· un assureur qui traîne peut être mis en demeure'],
      ['Les mentions obligatoires du contrat', '· une exclusion absente de la police ne t\'est pas opposable'],
      ['Le montant proposé face à la valeur réelle', ''],
      ['Le montant réclamé', 'face au barème officiel'],
    ],
    legalNote: 'Chaque vérification s\'appuie sur la loi belge sur les assurances. Chaque référence citée est vérifiée.',
    aiTail: 'Aucun chatbot générique ne lit ta lettre de refus comme elle.',
    bandLead: 'L\'IA analyse ton dossier en 7 passes.',
    faqTitle: 'Tes questions sur les litiges d\'assurance',
    faqs: [
      { q: 'Ils ont le droit de refuser ?', a: 'Seulement sur une exclusion prévue au contrat, et en la motivant précisément : une clause citée ne suffit pas. Scanne d\'abord : tu sauras si la lettre de refus est contestable et combien de temps tu as.' },
      { q: 'C\'est légal de contester ?', a: 'Oui, c\'est un droit. La contestation suit une procédure précise : Archer te la donne, adaptée à ta lettre de refus.' },
      { q: 'Et si mon dossier est solide côté adverse ?', a: 'Archer te le dit franchement. Renoncer peut alors être le bon calcul, et tu auras évité de payer un avocat pour l\'apprendre.' },
      { q: 'Combien ça coûte ?', a: 'Le scan et le verdict : gratuits, sans carte. Si tu veux qu\'Archer conteste pour toi : dès 34€/mois avec l’engagement 12 mois, 39€ sans engagement.' },
    ],
    finalCta: ['Ton recours a des délais.', 'Chaque jour compte.'],
    finalCtaBtn: 'Analyser mon refus · Gratuit',
    proof: {
      score: 66,
      ringOffset: 111,
      ringColor: '#f59e0b',
      kickerKind: 'a',
      kicker: '● REFUS CONTESTABLE',
      headline: 'La clause invoquée ne s\'applique pas.',
      headlineKind: 'a',
      summary: 'L\'assureur refuse ton sinistre en invoquant une exclusion pour défaut d\'entretien, mais aucun rapport d\'expertise ne vient l\'étayer. Ce refus repose sur une affirmation, pas une preuve.',
      kpis: [
        { label: 'Montant du sinistre', value: '3 200 €', kind: '', sub: 'valeur estimée' },
        { label: 'Ce que tu récupères', value: '3 200 €', kind: 'g', sub: 'si ça aboutit' },
        { label: 'Délai pour contester', value: '1 mois', kind: 'a', sub: 'avant prescription' },
      ],
      args: [
        { title: 'Refus non motivé par une expertise documentée', basis: 'Loi du 4 avril 2014, art. 62 · obligation de motivation', tag: 'SOLIDE', weak: false },
        { title: 'Absence de preuve du défaut d\'entretien invoqué', basis: 'Art. 1315 ancien Code civil · charge de la preuve', tag: 'SOLIDE', weak: false },
        { title: 'Délai de traitement du dossier dépassé', basis: 'Art. 22 Loi du 4 avril 2014', tag: 'MOYEN', weak: true },
      ],
      letter: [
        'La contestation du refus et la demande du rapport d\'expertise',
        'La demande d\'une contre-expertise indépendante',
        'La mise en demeure de réexaminer le dossier',
        'Le rappel des voies de recours (médiateur des assurances)',
      ],
      cta: 'Analyser mon refus · Gratuit',
      projection: [
        { label: 'Indemnisation obtenue', value: '38%', width: '38%', color: '#16a34a' },
        { label: 'Indemnisation partielle', value: '35%', width: '35%', color: '#f59e0b' },
        { label: 'Refus maintenu', value: '27%', width: '27%', color: '#e2e0db' },
      ],
      note: 'Et si le refus est fondé, Archer te l\'explique clairement, pour que tu saches à quoi t\'en tenir.',
      foot: ['ANALYSE GÉNÉRÉE EN 58 SECONDES', '4 RÉFÉRENCES LÉGALES VÉRIFIÉES · DROIT BELGE'],
      juristPrice: 'Dès 34 €/mois',
    },
  },
  'telecom': {
    badge: '📱 Télécom · Proximus, Orange, Telenet',
    h1: ['Ta facture télécom a gonflé ?', 'Ils comptent sur le fait que tu ne liras pas.'],
    dropzone: 'Glisse ta facture ici, ou clique',
    checksTitle: 'Ce qu\'Archer vérifie sur ta facture télécom',
    checksIntro: 'Un seul de ces défauts peut suffire à faire tomber les frais',
    checks: [
      ['Les frais de résiliation', '· souvent facturés alors que l\'engagement est fini'],
      ['Les options jamais souscrites', '· apparues sans accord signé'],
      ['La hausse de prix unilatérale', '· tu as un droit de résiliation sans frais'],
      ['Les mentions obligatoires de la facture télécom', '· détail des postes, tarifs et frais annexes'],
      ['La cohérence avec ton contrat d\'origine', ''],
      ['Le montant réclamé', 'face au barème officiel'],
    ],
    legalNote: 'Chaque vérification s\'appuie sur la Code de droit économique et loi télécom belge. Chaque référence citée est vérifiée.',
    aiTail: 'Aucun chatbot générique ne lit ta facture télécom comme elle.',
    bandLead: 'L\'IA analyse ta facture en 7 passes.',
    faqTitle: 'Tes questions sur les litiges télécom',
    faqs: [
      { q: 'Je dois payer tout de suite ?', a: 'Non. Et payer trop vite peut valoir acceptation. Scanne d\'abord : tu sauras si ta facture télécom est contestable et combien de temps tu as.' },
      { q: 'C\'est légal de contester ?', a: 'Oui, c\'est un droit. La contestation suit une procédure précise : Archer te la donne, adaptée à ta facture télécom.' },
      { q: 'Et si mon dossier est solide côté adverse ?', a: 'Archer te le dit franchement. Payer vite peut alors être ta meilleure option, et tu auras évité de payer un avocat pour l\'apprendre.' },
      { q: 'Combien ça coûte ?', a: 'Le scan et le verdict : gratuits, sans carte. Si tu veux qu\'Archer conteste pour toi : dès 34€/mois avec l’engagement 12 mois, 39€ sans engagement.' },
    ],
    finalCta: ['Ta facture a des délais.', 'Chaque jour compte.'],
    finalCtaBtn: 'Analyser ma facture · Gratuit',
    proof: {
      score: 76,
      ringOffset: 78,
      ringColor: '#16a34a',
      kickerKind: 'g',
      kicker: '● FRAIS CONTESTABLES',
      headline: '247 € que ton opérateur te doit.',
      headlineKind: 'g',
      summary: 'Des frais de résiliation te sont facturés alors que ta période d\'engagement est terminée depuis 3 mois, et des options apparaissent sur ta facture sans qu\'aucun accord signé ne les justifie.',
      kpis: [
        { label: 'Montant réclamé', value: '247 €', kind: '', sub: 'frais + options' },
        { label: 'Ce que tu récupères', value: '247 €', kind: 'g', sub: 'si la contestation aboutit' },
        { label: 'Délai pour contester', value: '30 jours', kind: 'a', sub: 'depuis la facture' },
      ],
      args: [
        { title: 'Frais de résiliation facturés après la fin de l\'engagement', basis: 'Art. 111/3 §3, Loi du 13 juin 2005 · gratuité après la période minimale', tag: 'SOLIDE', weak: false },
        { title: 'Options facturées sans preuve de souscription', basis: 'Art. VI.45 CDE · obligation d\'information et d\'accord préalable', tag: 'SOLIDE', weak: false },
        { title: 'Clause de renouvellement non portée à ta connaissance', basis: 'Art. VI.83 CDE · clauses abusives', tag: 'MOYEN', weak: true },
      ],
      letter: [
        'La contestation détaillée poste par poste',
        'La demande de preuve de souscription des options',
        'La demande de remboursement sous 30 jours',
        'La saisine du Service de médiation Télécom en cas de refus',
      ],
      cta: 'Analyser ma facture télécom · Gratuit',
      projection: [
        { label: 'Remboursement total', value: '52%', width: '52%', color: '#16a34a' },
        { label: 'Geste commercial', value: '26%', width: '26%', color: '#f59e0b' },
        { label: 'Refus', value: '22%', width: '22%', color: '#e2e0db' },
      ],
      note: 'Et si les frais sont dus, Archer te le dit clairement : tu paies ce qui est juste, rien de plus.',
      foot: ['ANALYSE GÉNÉRÉE EN 58 SECONDES', '4 RÉFÉRENCES LÉGALES VÉRIFIÉES · DROIT BELGE'],
      juristPrice: 'Dès 34 €/mois',
    },
  },
  'energie': {
    badge: '⚡ Énergie · régularisation',
    h1: ['1 480 € de régularisation ?', 'Vérifie avant de payer.'],
    dropzone: 'Glisse ta facture ici, ou clique',
    checksTitle: 'Ce qu\'Archer vérifie sur ta facture d\'énergie',
    checksIntro: 'Un seul de ces défauts peut suffire à faire tomber une partie du montant',
    checks: [
      ['Les index estimés vs relevés', '· une facture doit refléter ta consommation réelle'],
      ['La période facturée', '· au-delà de 5 ans, la consommation est prescrite'],
      ['Le tarif appliqué', '· conforme à ton contrat ou modifié unilatéralement'],
      ['Les mentions obligatoires de la facture d\'énergie', '· point de fourniture, période exacte et tarif appliqué'],
      ['Les frais annexes ajoutés', ''],
      ['Le montant réclamé', 'face au barème officiel'],
    ],
    legalNote: 'Chaque vérification s\'appuie sur la droit belge de la consommation et de l\'énergie. Chaque référence citée est vérifiée.',
    aiTail: 'Aucun chatbot générique ne lit ta facture d\'énergie comme elle.',
    bandLead: 'L\'IA analyse ta facture en 7 passes.',
    faqTitle: 'Tes questions sur les factures d\'énergie',
    faqs: [
      { q: 'Je dois payer tout de suite ?', a: 'Non. Et payer trop vite peut valoir acceptation. Scanne d\'abord : tu sauras si ta facture de régularisation est contestable et combien de temps tu as.' },
      { q: 'C\'est légal de contester ?', a: 'Oui, c\'est un droit. La contestation suit une procédure précise : Archer te la donne, adaptée à ta facture d\'énergie.' },
      { q: 'Et si mon dossier est solide côté adverse ?', a: 'Archer te le dit franchement. Payer vite peut alors être ta meilleure option, et tu auras évité de payer un avocat pour l\'apprendre.' },
      { q: 'Combien ça coûte ?', a: 'Le scan et le verdict : gratuits, sans carte. Si tu veux qu\'Archer conteste pour toi : dès 34€/mois avec l’engagement 12 mois, 39€ sans engagement.' },
    ],
    finalCta: ['Ta régularisation a des délais.', 'Chaque jour compte.'],
    finalCtaBtn: 'Analyser ma facture · Gratuit',
    proof: {
      score: 73,
      ringOffset: 88,
      ringColor: '#16a34a',
      kickerKind: 'g',
      kicker: '● RÉGULARISATION CONTESTABLE',
      headline: '1 480 € à vérifier de près.',
      headlineKind: 'g',
      summary: 'Ta régularisation annuelle repose sur des index estimés, pas relevés. Une partie de la période facturée dépasse le délai pendant lequel un fournisseur peut réclamer une consommation.',
      kpis: [
        { label: 'Montant réclamé', value: '1 480 €', kind: '', sub: 'régularisation annuelle' },
        { label: 'Potentiellement annulable', value: '620 €', kind: 'g', sub: 'période prescrite' },
        { label: 'Délai pour contester', value: '30 jours', kind: 'a', sub: 'depuis la facture' },
      ],
      args: [
        { title: 'Facturation basée sur des index estimés, jamais relevés', basis: 'Obligation de facturation sur consommation réelle', tag: 'SOLIDE', weak: false },
        { title: 'Une partie de la période réclamée est prescrite', basis: 'Art. 2277 ancien Code civil · prescription quinquennale des consommations', tag: 'SOLIDE', weak: false },
        { title: 'Absence de plan d\'apurement proposé avant mise en demeure', basis: 'Pratique encadrée du secteur énergie', tag: 'MOYEN', weak: true },
      ],
      letter: [
        'La demande du détail des index relevés vs estimés',
        'La contestation de la période prescrite',
        'La demande d\'un plan d\'apurement échelonné',
        'La saisine du médiateur de l\'énergie en cas de blocage',
      ],
      cta: 'Analyser ma facture énergie · Gratuit',
      projection: [
        { label: 'Facture corrigée', value: '44%', width: '44%', color: '#16a34a' },
        { label: 'Plan d\'apurement', value: '34%', width: '34%', color: '#f59e0b' },
        { label: 'Maintien', value: '22%', width: '22%', color: '#e2e0db' },
      ],
      note: 'Et si la consommation est réelle, Archer te le dit, et t\'aide à obtenir un étalement plutôt qu\'une mise en demeure.',
      foot: ['ANALYSE GÉNÉRÉE EN 58 SECONDES', '4 RÉFÉRENCES LÉGALES VÉRIFIÉES · DROIT BELGE'],
      juristPrice: 'Dès 34 €/mois',
    },
  },
  'sepa': {
    badge: '🏦 Prélèvements SEPA non autorisés',
    h1: ['Un prélèvement que tu ne reconnais pas ?', 'Tu peux exiger le remboursement.'],
    dropzone: 'Glisse ton relevé ici, ou clique',
    checksTitle: 'Ce qu\'Archer vérifie sur tes prélèvements',
    checksIntro: 'Un seul de ces points peut suffire à récupérer ton argent',
    checks: [
      ['L\'existence d\'un mandat signé', '· sans mandat, le prélèvement est non autorisé'],
      ['La fenêtre de 8 semaines', '· remboursement sans justification à demander'],
      ['Les prélèvements non autorisés', '· remboursables jusqu\'à 13 mois'],
      ['Les mentions obligatoires du mandat de domiciliation', '· référence unique, identifiant du créancier, date et signature'],
      ['L\'identité réelle du créancier', ''],
      ['Le montant réclamé', 'face au barème officiel'],
    ],
    legalNote: 'Chaque vérification s\'appuie sur la droit belge et européen des services de paiement. Chaque référence citée est vérifiée.',
    aiTail: 'Aucun chatbot générique ne lit ton relevé comme elle.',
    bandLead: 'L\'IA analyse ton relevé en 7 passes.',
    faqTitle: 'Tes questions sur les prélèvements',
    faqs: [
      { q: 'Je peux récupérer mon argent ?', a: 'Oui, sans avoir à te justifier dans les 8 semaines, et jusqu\'à 13 mois si le prélèvement n\'était pas autorisé. Scanne d\'abord : tu sauras si tes prélèvements sont contestables et combien de temps tu as.' },
      { q: 'C\'est légal de demander le remboursement ?', a: 'Oui, c\'est un droit. La demande suit une procédure précise : Archer te la donne, adaptée à ton relevé et au mandat de domiciliation.' },
      { q: 'Et si mon dossier est solide côté adverse ?', a: 'Archer te le dit franchement. Renoncer peut alors être le bon calcul, et tu auras évité de payer un avocat pour l\'apprendre.' },
      { q: 'Combien ça coûte ?', a: 'Le scan et le verdict : gratuits, sans carte. Si tu veux qu\'Archer réclame pour toi : dès 34€/mois avec l’engagement 12 mois, 39€ sans engagement.' },
    ],
    finalCta: ['Tes prélèvements ont des délais.', 'Chaque jour compte.'],
    finalCtaBtn: 'Analyser mes prélèvements · Gratuit',
    proof: {
      score: 84,
      ringOffset: 52,
      ringColor: '#16a34a',
      kickerKind: 'g',
      kicker: '● REMBOURSEMENT DE PLEIN DROIT',
      headline: 'Ta banque doit te rembourser.',
      headlineKind: 'g',
      summary: 'Des prélèvements SEPA apparaissent sur ton compte sans mandat signé retrouvable. Pour un prélèvement SEPA autorisé, tu disposes de 8 semaines pour demander le remboursement sans justification.',
      kpis: [
        { label: 'Montant prélevé', value: '2 400 €', kind: '', sub: 'sur 12 mois' },
        { label: 'Récupérable immédiatement', value: '780 €', kind: 'g', sub: 'dans la fenêtre de 8 semaines' },
        { label: 'Délai à respecter', value: '8 semaines', kind: 'a', sub: 'par prélèvement' },
      ],
      args: [
        { title: 'Aucun mandat de prélèvement signé ne peut être produit', basis: 'Règlement (UE) 260/2012 · le mandat est obligatoire', tag: 'SOLIDE', weak: false },
        { title: 'Droit au remboursement sans justification sous 8 semaines', basis: 'Livre VII CDE · services de paiement', tag: 'SOLIDE', weak: false },
        { title: 'Prélèvements non autorisés : remboursement possible jusqu\'à 13 mois', basis: 'Livre VII CDE · opérations non autorisées', tag: 'MOYEN', weak: true },
      ],
      letter: [
        'La demande de remboursement immédiat auprès de ta banque',
        'La contestation des prélèvements auprès du créancier',
        'La demande de production du mandat SEPA signé',
        'La révocation formelle du mandat pour l\'avenir',
      ],
      cta: 'Analyser mes prélèvements · Gratuit',
      projection: [
        { label: 'Remboursement obtenu', value: '60%', width: '60%', color: '#16a34a' },
        { label: 'Remboursement partiel', value: '22%', width: '22%', color: '#f59e0b' },
        { label: 'Litige à poursuivre', value: '18%', width: '18%', color: '#e2e0db' },
      ],
      note: 'Et si le mandat existe bien, Archer te le dit, et t\'explique comment le révoquer proprement.',
      foot: ['ANALYSE GÉNÉRÉE EN 58 SECONDES', '4 RÉFÉRENCES LÉGALES VÉRIFIÉES · DROIT BELGE'],
      juristPrice: 'Dès 34 €/mois',
    },
  },
  'vitesse': {
    badge: '📷 Excès de vitesse · radar, lidar, zone 30',
    h1: ['Flashé ?', 'Ne paie pas avant de savoir.'],
    dropzone: 'Glisse ton PV ici, ou clique',
    checksTitle: 'Ce qu\'Archer vérifie sur ton PV de vitesse',
    checksIntro: 'Un seul de ces défauts peut suffire à faire tomber le PV',
    checks: [
      ['Le certificat d\'étalonnage de l\'appareil', '· périmé, le PV devient fragile'],
      ['La signalisation de la zone', '· absente ou non répétée, ça se conteste, et souvent oubliée'],
      ['La marge de tolérance appliquée', '· déduite du relevé, elle est obligatoire'],
      ['Les mentions obligatoires du procès-verbal', '· appareil utilisé, vitesse mesurée et vitesse retenue'],
      ['Le délai d\'envoi de la copie du PV', ''],
      ['Le montant réclamé', 'face au barème officiel'],
    ],
    legalNote: 'Chaque vérification s\'appuie sur la législation routière belge. Chaque référence citée est vérifiée.',
    aiTail: 'Aucun chatbot générique ne lit ton PV comme elle.',
    bandLead: 'L\'IA analyse ton PV en 7 passes.',
    faqTitle: 'Tes questions sur les PV de vitesse',
    faqs: [
      { q: 'Je dois payer tout de suite ?', a: 'Non. Et payer trop vite peut valoir acceptation. Scanne d\'abord : tu sauras si ton PV est contestable et combien de temps tu as.' },
      { q: 'C\'est légal de contester ?', a: 'Oui, c\'est un droit. La contestation suit une procédure précise : Archer te la donne, adaptée à ton PV.' },
      { q: 'Et si mon dossier est solide côté adverse ?', a: 'Archer te le dit franchement. Payer vite peut alors être ta meilleure option, et tu auras évité de payer un avocat pour l\'apprendre.' },
      { q: 'Combien ça coûte ?', a: 'Le scan et le verdict : gratuits, sans carte. Si tu veux qu\'Archer conteste pour toi : dès 34€/mois avec l’engagement 12 mois, 39€ sans engagement.' },
    ],
    finalCta: ['Ton PV a des délais.', 'Chaque jour compte.'],
    finalCtaBtn: 'Analyser mon PV · Gratuit',
    proof: {
      score: 78,
      ringOffset: 72,
      ringColor: '#16a34a',
      kickerKind: 'g',
      kicker: '● PV CONTESTABLE',
      headline: 'Trois failles exploitables.',
      headlineKind: 'g',
      summary: 'Ton PV présente des défauts de forme réels : le certificat d\'étalonnage du radar n\'est pas mentionné, la marge de tolérance semble mal appliquée, et la signalisation de la zone n\'est pas justifiée.',
      kpis: [
        { label: 'Montant réclamé', value: '179 €', kind: '', sub: 'amende + frais' },
        { label: 'Si le PV tombe', value: '0 €', kind: 'g', sub: 'tu ne paies rien' },
        { label: 'Délai pour contester', value: '12 jours', kind: 'a', sub: 'depuis ton document' },
      ],
      args: [
        { title: 'Le certificat de vérification annuelle du cinémomètre n\'est pas produit', basis: 'AR du 12 octobre 2010 · homologation et contrôle annuel', tag: 'SOLIDE', weak: false },
        { title: 'La marge de tolérance technique ne semble pas appliquée', basis: 'AR 12/10/2010 · la vitesse retenue doit être corrigée', tag: 'SOLIDE', weak: false },
        { title: 'La signalisation de la zone n\'est pas établie dans le PV', basis: 'Art. 62 Loi du 16 mars 1968 · force probante du PV', tag: 'MOYEN', weak: true },
      ],
      letter: [
        'La demande du certificat d\'étalonnage du radar',
        'Le recalcul de la vitesse après marge de tolérance',
        'La contestation formelle dans le délai légal, en recommandé',
        'La demande de classement sans suite motivée',
      ],
      cta: 'Analyser mon PV · Gratuit',
      projection: [
        { label: 'Annulation', value: '45%', width: '45%', color: '#16a34a' },
        { label: 'Réduction', value: '30%', width: '30%', color: '#f59e0b' },
        { label: 'Maintien', value: '25%', width: '25%', color: '#e2e0db' },
      ],
      note: 'Et si ton PV est solide, Archer te le dit franchement : tu paies vite, sans frais en plus.',
      foot: ['ANALYSE GÉNÉRÉE EN 58 SECONDES', '4 RÉFÉRENCES LÉGALES VÉRIFIÉES · DROIT BELGE'],
      juristPrice: 'Dès 34 €/mois',
    },
  },
  'telephone-volant': {
    badge: '📵 Téléphone au volant',
    h1: ['Verbalisé pour ton téléphone ?', 'Vérifie ce que le PV prouve vraiment.'],
    dropzone: 'Glisse ton PV ici, ou clique',
    checksTitle: 'Ce qu\'Archer vérifie sur ton PV',
    checksIntro: 'Un seul de ces défauts peut suffire à faire tomber le PV',
    checks: [
      ['La description précise de l\'usage constaté', '· tenir ≠ téléphoner : la nuance est juridique'],
      ['Les éléments matériels joints', '· photo, vidéo, ou simple affirmation ?'],
      ['La situation du véhicule', '· à l\'arrêt dans un endroit autorisé, l\'infraction tombe'],
      ['Les mentions obligatoires du procès-verbal', '· description précise des faits constatés, date et lieu'],
      ['Le degré d\'infraction retenu', ''],
      ['Le montant réclamé', 'face au barème officiel'],
    ],
    legalNote: 'Chaque vérification s\'appuie sur la Code de la route belge. Chaque référence citée est vérifiée.',
    aiTail: 'Aucun chatbot générique ne lit ton PV comme elle.',
    bandLead: 'L\'IA analyse ton PV en 7 passes.',
    faqTitle: 'Tes questions sur le téléphone au volant',
    faqs: [
      { q: 'Je dois payer tout de suite ?', a: 'Non. Et payer trop vite peut valoir acceptation. Scanne d\'abord : tu sauras si ton PV est contestable et combien de temps tu as.' },
      { q: 'C\'est légal de contester ?', a: 'Oui, c\'est un droit. La contestation suit une procédure précise : Archer te la donne, adaptée à ton PV.' },
      { q: 'Et si mon dossier est solide côté adverse ?', a: 'Archer te le dit franchement. Payer vite peut alors être ta meilleure option, et tu auras évité de payer un avocat pour l\'apprendre.' },
      { q: 'Combien ça coûte ?', a: 'Le scan et le verdict : gratuits, sans carte. Si tu veux qu\'Archer conteste pour toi : dès 34€/mois avec l’engagement 12 mois, 39€ sans engagement.' },
    ],
    finalCta: ['Ton PV a des délais.', 'Chaque jour compte.'],
    finalCtaBtn: 'Analyser mon PV · Gratuit',
    proof: {
      score: 74,
      ringOffset: 85,
      ringColor: '#16a34a',
      kickerKind: 'g',
      kicker: '● CONSTAT CONTESTABLE',
      headline: 'Tenir n\'est pas téléphoner.',
      headlineKind: 'g',
      summary: 'Le procès-verbal affirme l\'usage du téléphone sans décrire précisément ce qui a été constaté. Or l\'infraction suppose l\'usage effectif de l\'appareil tenu en main, pas sa simple présence.',
      kpis: [
        { label: 'Montant réclamé', value: '174 €', kind: '', sub: 'amende de 2e degré' },
        { label: 'Si le PV tombe', value: '0 €', kind: 'g', sub: 'tu ne paies rien' },
        { label: 'Délai pour contester', value: '15 jours', kind: 'a', sub: 'depuis la notification' },
      ],
      args: [
        { title: 'Le PV ne décrit pas précisément l\'usage constaté', basis: 'Art. 8.4 Code de la route · usage de l\'appareil tenu en main', tag: 'SOLIDE', weak: false },
        { title: 'Aucune photo ni élément matériel ne vient étayer le constat', basis: 'Art. 62 Loi du 16 mars 1968 · force probante limitée aux constatations', tag: 'SOLIDE', weak: false },
        { title: 'Le véhicule pouvait être à l\'arrêt dans un endroit autorisé', basis: 'Art. 8.4 · l\'infraction suppose la conduite', tag: 'MOYEN', weak: true },
      ],
      letter: [
        'La demande du procès-verbal complet et de ses annexes',
        'La contestation de la matérialité des faits constatés',
        'La demande d\'accès aux éventuelles images',
        'La demande de classement sans suite motivée',
      ],
      cta: 'Analyser mon PV · Gratuit',
      projection: [
        { label: 'Annulation', value: '40%', width: '40%', color: '#16a34a' },
        { label: 'Réduction', value: '28%', width: '28%', color: '#f59e0b' },
        { label: 'Maintien', value: '32%', width: '32%', color: '#e2e0db' },
      ],
      note: 'Et si le constat est solide, Archer te le dit : tu paies vite, sans frais supplémentaires.',
      foot: ['ANALYSE GÉNÉRÉE EN 58 SECONDES', '4 RÉFÉRENCES LÉGALES VÉRIFIÉES · DROIT BELGE'],
      juristPrice: 'Dès 34 €/mois',
    },
  },
  'jamais-livre': {
    badge: '📦 Jamais livré, jamais remboursé',
    h1: ['Vinted, faux site, vendeur fantôme ?', 'Tu as plus de droits que tu ne crois.'],
    dropzone: 'Glisse ta preuve d\'achat ici, ou clique',
    checksTitle: 'Ce qu\'Archer vérifie sur ta commande',
    checksIntro: 'Un seul de ces points peut suffire à obtenir ton remboursement',
    checks: [
      ['Le délai de livraison légal', '· 30 jours maximum, au-delà tu peux exiger le remboursement'],
      ['Le droit de rétractation', '· 14 jours, même sans motif'],
      ['La garantie légale de conformité', '· 2 ans, même si le vendeur ne la mentionne pas'],
      ['Les mentions obligatoires du vendeur en ligne', '· identité, adresse et numéro d\'entreprise vérifiables'],
      ['Le mode de paiement et tes recours bancaires', ''],
      ['Le montant réclamé', 'face au barème officiel'],
    ],
    legalNote: 'Chaque vérification s\'appuie sur la Code de droit économique belge. Chaque référence citée est vérifiée.',
    aiTail: 'Aucun chatbot générique ne lit ta commande comme elle.',
    bandLead: 'L\'IA analyse ton dossier en 7 passes.',
    faqTitle: 'Tes questions sur les achats non livrés',
    faqs: [
      { q: 'Je peux annuler et être remboursé ?', a: 'Oui : passé 30 jours sans livraison, tu peux exiger la résolution de la commande et le remboursement intégral. Scanne d\'abord : tu sauras si ta commande ouvre droit à un remboursement et combien de temps tu as.' },
      { q: 'J\'ai le droit d\'exiger le remboursement ?', a: 'Oui, c\'est un droit. La réclamation suit une procédure précise : Archer te la donne, adaptée à ta commande et à ta preuve d\'achat.' },
      { q: 'Et si mon dossier est solide côté adverse ?', a: 'Archer te le dit franchement. Renoncer peut alors être le bon calcul, et tu auras évité de payer un avocat pour l\'apprendre.' },
      { q: 'Combien ça coûte ?', a: 'Le scan et le verdict : gratuits, sans carte. Si tu veux qu\'Archer réclame pour toi : dès 34€/mois avec l’engagement 12 mois, 39€ sans engagement.' },
    ],
    finalCta: ['Ton dossier a des délais.', 'Chaque jour compte.'],
    finalCtaBtn: 'Analyser ma commande · Gratuit',
    proof: {
      score: 81,
      ringOffset: 62,
      ringColor: '#16a34a',
      kickerKind: 'g',
      kicker: '● REMBOURSEMENT DÛ',
      headline: 'Le vendeur est en tort, pas toi.',
      headlineKind: 'g',
      summary: 'Ta commande devait être livrée sous 30 jours : 52 jours se sont écoulés sans nouvelle. Le droit de résolution du contrat est ouvert, avec remboursement intégral.',
      kpis: [
        { label: 'Montant payé', value: '389 €', kind: '', sub: 'commande non livrée' },
        { label: 'Ce que tu récupères', value: '389 €', kind: 'g', sub: 'remboursement intégral' },
        { label: 'Délai pour agir', value: 'Immédiat', kind: 'a', sub: 'délai légal dépassé' },
      ],
      args: [
        { title: 'Livraison non effectuée après 52 jours', basis: 'Art. VI.43 CDE · obligation de livraison sous 30 jours', tag: 'SOLIDE', weak: false },
        { title: 'Aucune réponse du vendeur aux relances', basis: 'Art. VI.44 CDE · droit à la résolution du contrat', tag: 'SOLIDE', weak: false },
        { title: 'Absence de mentions légales vérifiables sur le site', basis: 'Art. VI.45 CDE · obligation d\'information', tag: 'MOYEN', weak: true },
      ],
      letter: [
        'La mise en demeure de livrer sous 8 jours',
        'La demande de résolution et remboursement intégral',
        'La référence aux articles applicables du CDE',
        'La procédure de contestation bancaire si silence',
      ],
      cta: 'Analyser ma commande · Gratuit',
      projection: [
        { label: 'Remboursement obtenu', value: '55%', width: '55%', color: '#16a34a' },
        { label: 'Remboursement partiel', value: '20%', width: '20%', color: '#f59e0b' },
        { label: 'Sans réponse', value: '25%', width: '25%', color: '#e2e0db' },
      ],
      note: 'Et si ta commande est encore dans les délais, Archer te le dit, pas de démarche inutile.',
      foot: ['ANALYSE GÉNÉRÉE EN 58 SECONDES', '4 RÉFÉRENCES LÉGALES VÉRIFIÉES · DROIT BELGE'],
      juristPrice: 'Dès 34 €/mois',
    },
  },
  'abonnement-piege': {
    badge: '🔁 Abonnement piège',
    h1: ['Un essai gratuit qui te facture ?', 'Tu n\'as peut-être jamais donné ton accord.'],
    dropzone: 'Glisse ton relevé ou l\'email ici, ou clique',
    checksTitle: 'Ce qu\'Archer vérifie sur ton abonnement',
    checksIntro: 'Un seul de ces points peut suffire à récupérer tes prélèvements',
    checks: [
      ['Ton consentement explicite au paiement', '· un essai gratuit ne vaut pas accord d\'achat'],
      ['L\'information préalable sur le renouvellement', '· obligatoire, et souvent absente'],
      ['La facilité réelle de résiliation', '· une résiliation rendue difficile est contestable'],
      ['Les mentions obligatoires de la clause de reconduction', '· en gras, dans un cadre distinct, en première page'],
      ['Les conditions générales acceptées ou non', ''],
      ['Le montant réclamé', 'face au barème officiel'],
    ],
    legalNote: 'Chaque vérification s\'appuie sur la Code de droit économique belge. Chaque référence citée est vérifiée.',
    aiTail: 'Aucun chatbot générique ne lit ton abonnement comme elle.',
    bandLead: 'L\'IA analyse ton dossier en 7 passes.',
    faqTitle: 'Tes questions sur les abonnements pièges',
    faqs: [
      { q: 'Je peux résilier maintenant ?', a: 'Oui. Et si ton accord explicite au paiement n\'est pas démontrable, tu peux aussi réclamer les sommes déjà prélevées. Scanne d\'abord : tu sauras si ton abonnement a été valablement conclu et combien de temps tu as.' },
      { q: 'J\'ai le droit de réclamer ce qui a été prélevé ?', a: 'Oui, c\'est un droit. La réclamation suit une procédure précise : Archer te la donne, adaptée à ton abonnement et à tes prélèvements.' },
      { q: 'Et si mon dossier est solide côté adverse ?', a: 'Archer te le dit franchement. Renoncer peut alors être le bon calcul, et tu auras évité de payer un avocat pour l\'apprendre.' },
      { q: 'Combien ça coûte ?', a: 'Le scan et le verdict : gratuits, sans carte. Si tu veux qu\'Archer réclame pour toi : dès 34€/mois avec l’engagement 12 mois, 39€ sans engagement.' },
    ],
    finalCta: ['Tes prélèvements continuent.', 'Chaque jour compte.'],
    finalCtaBtn: 'Analyser mon abonnement · Gratuit',
    proof: {
      score: 79,
      ringOffset: 69,
      ringColor: '#16a34a',
      kickerKind: 'g',
      kicker: '● PRÉLÈVEMENT CONTESTABLE',
      headline: 'Tu n\'as jamais accepté ça.',
      headlineKind: 'g',
      summary: 'Un essai gratuit s\'est transformé en abonnement payant sans que ton accord explicite au paiement puisse être démontré, et l\'information préalable sur le renouvellement n\'apparaît nulle part.',
      kpis: [
        { label: 'Prélevé chaque mois', value: '79,99 €', kind: '', sub: 'depuis 4 mois' },
        { label: 'Récupérable', value: '319,96 €', kind: 'g', sub: 'total prélevé' },
        { label: 'Délai à respecter', value: '8 semaines', kind: 'a', sub: 'par prélèvement SEPA' },
      ],
      args: [
        { title: 'Aucun consentement explicite au paiement après l\'essai', basis: 'Art. VI.45 CDE · obligation d\'accord exprès à l\'obligation de paiement', tag: 'SOLIDE', weak: false },
        { title: 'Information préalable sur le renouvellement absente', basis: 'Livre VI CDE · pratiques commerciales déloyales', tag: 'SOLIDE', weak: false },
        { title: 'Procédure de résiliation rendue difficilement accessible', basis: 'Livre VI CDE · clauses et pratiques abusives', tag: 'MOYEN', weak: true },
      ],
      letter: [
        'La résiliation immédiate et sans frais de l\'abonnement',
        'La demande de remboursement des sommes prélevées',
        'La demande de preuve de ton consentement au paiement',
        'La révocation du mandat de prélèvement auprès de ta banque',
      ],
      cta: 'Analyser mon abonnement · Gratuit',
      projection: [
        { label: 'Remboursement obtenu', value: '46%', width: '46%', color: '#16a34a' },
        { label: 'Résiliation + geste', value: '32%', width: '32%', color: '#f59e0b' },
        { label: 'Résiliation seule', value: '22%', width: '22%', color: '#e2e0db' },
      ],
      note: 'Et si tu as bien accepté, Archer te le dit, et t\'explique comment résilier proprement, sans frais.',
      foot: ['ANALYSE GÉNÉRÉE EN 58 SECONDES', '4 RÉFÉRENCES LÉGALES VÉRIFIÉES · DROIT BELGE'],
      juristPrice: 'Dès 34 €/mois',
    },
  },
  'sinistre-refuse': {
    badge: '🛡️ Sinistre refusé',
    h1: ['Refusé par une lettre type ?', 'Fais vérifier leur refus.'],
    dropzone: 'Glisse la lettre de refus ici, ou clique',
    checksTitle: 'Ce qu\'Archer vérifie sur ta lettre de refus',
    checksIntro: 'Un seul de ces défauts peut suffire à faire tomber le refus',
    checks: [
      ['La motivation du refus', '· doit être précise et documentée, pas une clause citée'],
      ['La clause d\'exclusion invoquée', '· sa validité et son application réelle à ton cas'],
      ['La preuve apportée par l\'assureur', '· c\'est à lui de prouver l\'exclusion, pas à toi'],
      ['Les mentions obligatoires de la police', '· les exclusions doivent y être limitativement énumérées'],
      ['Ton droit à une contre-expertise', ''],
      ['Le montant réclamé', 'face au barème officiel'],
    ],
    legalNote: 'Chaque vérification s\'appuie sur la loi belge sur les assurances. Chaque référence citée est vérifiée.',
    aiTail: 'Aucun chatbot générique ne lit ta lettre de refus comme elle.',
    bandLead: 'L\'IA analyse ton dossier en 7 passes.',
    faqTitle: 'Tes questions sur les refus d\'assurance',
    faqs: [
      { q: 'Ils ont le droit de refuser ?', a: 'Seulement sur une exclusion prévue au contrat, et c\'est à l\'assureur de la prouver, pas à toi. Scanne d\'abord : tu sauras si la lettre de refus est contestable et combien de temps tu as.' },
      { q: 'C\'est légal de contester ?', a: 'Oui, c\'est un droit. La contestation suit une procédure précise : Archer te la donne, adaptée à ta lettre de refus.' },
      { q: 'Et si mon dossier est solide côté adverse ?', a: 'Archer te le dit franchement. Renoncer peut alors être le bon calcul, et tu auras évité de payer un avocat pour l\'apprendre.' },
      { q: 'Combien ça coûte ?', a: 'Le scan et le verdict : gratuits, sans carte. Si tu veux qu\'Archer conteste pour toi : dès 34€/mois avec l’engagement 12 mois, 39€ sans engagement.' },
    ],
    finalCta: ['Ton recours a des délais.', 'Chaque jour compte.'],
    finalCtaBtn: 'Analyser mon refus · Gratuit',
    proof: {
      score: 66,
      ringOffset: 111,
      ringColor: '#f59e0b',
      kickerKind: 'a',
      kicker: '● REFUS CONTESTABLE',
      headline: 'La clause invoquée ne s\'applique pas.',
      headlineKind: 'a',
      summary: 'L\'assureur refuse ton sinistre en invoquant une exclusion pour défaut d\'entretien, mais aucun rapport d\'expertise ne vient l\'étayer. Ce refus repose sur une affirmation, pas une preuve.',
      kpis: [
        { label: 'Montant du sinistre', value: '3 200 €', kind: '', sub: 'valeur estimée' },
        { label: 'Ce que tu récupères', value: '3 200 €', kind: 'g', sub: 'si ça aboutit' },
        { label: 'Délai pour contester', value: '1 mois', kind: 'a', sub: 'avant prescription' },
      ],
      args: [
        { title: 'Refus non motivé par une expertise documentée', basis: 'Loi du 4 avril 2014, art. 62 · obligation de motivation', tag: 'SOLIDE', weak: false },
        { title: 'Absence de preuve du défaut invoqué', basis: 'Art. 1315 ancien Code civil · la charge de la preuve incombe à l\'assureur', tag: 'SOLIDE', weak: false },
        { title: 'Délai de traitement du dossier dépassé', basis: 'Art. 22 Loi du 4 avril 2014 · délai raisonnable', tag: 'MOYEN', weak: true },
      ],
      letter: [
        'La contestation du refus et la demande du rapport d\'expertise',
        'La demande d\'une contre-expertise indépendante',
        'La mise en demeure de réexaminer le dossier',
        'Le rappel des voies de recours (médiateur des assurances)',
      ],
      cta: 'Analyser mon refus · Gratuit',
      projection: [
        { label: 'Indemnisation obtenue', value: '38%', width: '38%', color: '#16a34a' },
        { label: 'Indemnisation partielle', value: '35%', width: '35%', color: '#f59e0b' },
        { label: 'Refus maintenu', value: '27%', width: '27%', color: '#e2e0db' },
      ],
      note: 'Et si le refus est fondé, Archer te l\'explique clairement, pour que tu saches à quoi t\'en tenir.',
      foot: ['ANALYSE GÉNÉRÉE EN 58 SECONDES', '4 RÉFÉRENCES LÉGALES VÉRIFIÉES · DROIT BELGE'],
      juristPrice: 'Dès 34 €/mois',
    },
  },
};

export const LANDING_SLUGS = Object.keys(LANDING_THEMES);
